import { useCallback } from 'react';

import type { BuyNowInitializer } from '@ctct/application-layer';
import { BillingStrategy, createResolvedBillingStrategy, RatingCallRequest, PriceVO } from '@ctct/domain-layer';
import { AccountModificationFlowId, type RatingPayload } from '@ctct/infrastructure-layer';
import { Card } from '@ctct/payflow-ui';
import {
  AlertWidget,
  BillingInfoFormWidget,
  ContactsTierSelectWidget,
  CurrencyWidget,
  EmailPlansWidget,
  NonProfitWidget,
  OrderSummaryWidget,
  PaymentButtonWidget,
  PaymentMethodsWidget,
  PrepaySelectWidget,
  PricingConfiguratorWidget,
  PromoCodesWidget,
  useIsMobile,
  useRouterNonUiWidget,
  usePrepaySelectWidget,
  useOrderSummaryWidget,
  usePaymentButtonWidget,
  useContactsTierSelectWidget,
  useCurrencyWidget,
  useNonProfitWidget,
  usePricingConfiguratorWidget,
  usePricingConfiguratorVisibility,
  PaymentProcessingInfoWidget,
  usePaymentProcessingInfoWidget,
  usePromoCodesWidget,
  useAlertWidget,
  usePaymentMethodsWidget,
  useEmailPlansWidget,
  useSessionStorageZustandBeforeUnload,
  SessionStorageKeys,
  isApplePayParamEnabled,
  useBillingInfoFormWidget,
  isStripeParamEnabled,
  isStripePhase2ParamEnabled,
  useInboundPromoCodesWidget,
  defaultUseSmsBundleTier,
} from '@ctct/react-app-layer';
import type { IBuyNowPageStore } from '@ctct/state-client-layer';
import {
  createContactsTierSelectHooks,
  createNonProfitHooks,
  createPrepaySelectHooks,
  createCurrencyHooks,
  useBuyNowPageStore,
  createPaymentProcessingInfoHooks,
  createPromoCodesHooks,
  createPaymentButtonHooks,
  createOrderSummaryHooks,
  createAlertHooks,
  createPaymentMethodsHooks,
  createEmailPlansHooks,
  createBillingInfoFormHooks,
} from '@ctct/state-client-layer';
import { useAccountId, usePaymentStrategy, usePlanGroupFacade } from '@ctct/state-server-layer';

import './buy-now.scss';

// Define the serialized type for session storage
type SerializedBuyNowPageStore = Omit<IBuyNowPageStore, 'ratingPayload' | 'planBaseMonthlyPrice'> & {
  ratingPayload: RatingPayload | null;
  planBaseMonthlyPrice: null | {
    value: number;
    currency: string;
  };
};

// store hooks
const prepaySelectHooks = createPrepaySelectHooks(useBuyNowPageStore);
const contactsTierSelectHooks = createContactsTierSelectHooks(useBuyNowPageStore);
const nonProfitHooks = createNonProfitHooks(useBuyNowPageStore);
const currencyHooks = createCurrencyHooks(useBuyNowPageStore);
const paymentProcessingInfoHooks = createPaymentProcessingInfoHooks(useBuyNowPageStore);
const promoCodesHooks = createPromoCodesHooks(useBuyNowPageStore);
const paymentButtonHooks = createPaymentButtonHooks(useBuyNowPageStore);
const orderSummaryHooks = createOrderSummaryHooks(useBuyNowPageStore);
const alertHooks = createAlertHooks(useBuyNowPageStore);
const paymentMethodsHooks = createPaymentMethodsHooks(useBuyNowPageStore);
const emailPlansHooks = createEmailPlansHooks(useBuyNowPageStore);
const billingInfoFormHooks = createBillingInfoFormHooks(useBuyNowPageStore);

// external hooks
const emailPlansExternalHooks = {
  useContactsTier: contactsTierSelectHooks.useContactsTier,
  usePrepay: prepaySelectHooks.usePrepay,
  useSmsBundleTier: defaultUseSmsBundleTier,
};

const orderSummaryExternalHooks = {
  useSelectedEmailPlanId: emailPlansHooks.useSelectedEmailPlanId,
  useContactsTier: contactsTierSelectHooks.useContactsTier,
  usePrepay: prepaySelectHooks.usePrepay,
  useSelectedAddOns: () => stableEmptyArray,
  useHasPendingExternalRedirect: paymentMethodsHooks.useHasPendingExternalRedirect,
  useIsPromoValidationPending: promoCodesHooks.usePromoCodeValidationPending,
};

const promoCodesExternalHooks = {
  useRatingCallPayload: orderSummaryHooks.useRatingCallPayload,
  useSelectedEmailPlanId: emailPlansHooks.useSelectedEmailPlanId,
  usePrepay: prepaySelectHooks.usePrepay,
};

const paymentButtonExternalHooks = {
  usePrepay: prepaySelectHooks.usePrepay,
  useHasPendingExternalRedirect: paymentMethodsHooks.useHasPendingExternalRedirect,
};

const paymentMethodsExternalHooks = {
  useRatingCallPayload: orderSummaryHooks.useRatingCallPayload,
};

const routerNonUiExternalHooks = {
  useIsHidePromoCodesEnabled: promoCodesHooks.useIsHidePromoCodesEnabled,
};

interface Props {
  initializer: BuyNowInitializer;
}

const stableEmptyArray = [];

export const BuyNowPage = ({ initializer }: Props) => {
  const { data: accountId } = useAccountId();
  const isStripeEnabled = isStripeParamEnabled() ?? false;
  const isStripePhase2Enabled = isStripePhase2ParamEnabled();
  const billingStrategy = createResolvedBillingStrategy(
    isStripeEnabled ? BillingStrategy.Stripe : BillingStrategy.InHouse
  );
  const paymentStrategy = usePaymentStrategy({ billingStrategyQuery: billingStrategy });
  const planGroupData = usePlanGroupFacade();
  const isTrialer = planGroupData.data?.isTrialer();
  const {
    shouldHidePricingConfigurator,
    shouldHideCurrencyWidget,
    shouldHideContactsTierSelectWidget,
    shouldHideNonProfitWidget,
    shouldHidePrepaySelectWidget,
  } = usePricingConfiguratorVisibility();

  const transformSaveState = useCallback(
    (state: IBuyNowPageStore): SerializedBuyNowPageStore => ({
      ...state,
      alerts: [],
      promoCodeFormErrors: [],
      planBaseMonthlyPrice: state.planBaseMonthlyPrice
        ? { ...state.planBaseMonthlyPrice, value: state.planBaseMonthlyPrice.value.priceAmount }
        : null,
      paymentMethodsFullLoading: false,
      paymentMethodsExternalRedirectLoading: false,
      ratingPayload: state.ratingPayload?.toJson() ?? null,
      ...(isStripeEnabled
        ? {
            paypalAgreementId: null,
          }
        : null),
    }),
    [isStripeEnabled]
  );

  const transformLoadState = useCallback(
    (state: SerializedBuyNowPageStore): IBuyNowPageStore => ({
      ...state,
      planBaseMonthlyPrice: state.planBaseMonthlyPrice
        ? { ...state.planBaseMonthlyPrice, value: new PriceVO(state.planBaseMonthlyPrice.value) }
        : null,
      paymentMethodsExternalRedirectLoading: false,
      ratingPayload: state.ratingPayload ? RatingCallRequest.fromJson(state.ratingPayload) : null,
    }),
    []
  );

  useSessionStorageZustandBeforeUnload(
    SessionStorageKeys.buynow_zustand_store,
    accountId,
    useBuyNowPageStore,
    transformSaveState,
    transformLoadState
  );

  const { isMobile } = useIsMobile();
  const isDesktop = !isMobile;

  const isApplePayEnabled = isApplePayParamEnabled();

  const contactsTierSelectWidgetProps = useContactsTierSelectWidget({
    initializer,
    pageStore: useBuyNowPageStore,
    hooks: contactsTierSelectHooks,
    billingStrategy,
  });
  const currencyWidgetProps = useCurrencyWidget({
    initializer,
    pageStore: useBuyNowPageStore,
    hooks: currencyHooks,
    billingStrategy,
  });
  const nonProfitWidgetProps = useNonProfitWidget({
    initializer,
    pageStore: useBuyNowPageStore,
    hooks: nonProfitHooks,
    billingStrategy,
  });
  const paymentProcessingInfoWidgetProps = usePaymentProcessingInfoWidget({
    initializer,
    pageStore: useBuyNowPageStore,
    hooks: paymentProcessingInfoHooks,
  });

  const promoCodesWidgetProps = usePromoCodesWidget({
    initializer,
    pageStore: useBuyNowPageStore,
    hooks: promoCodesHooks,
    externalHooks: promoCodesExternalHooks,
    billingStrategy,
  });
  const promoCodesWidget = promoCodesWidgetProps.data.shouldHide() ? null : (
    <PromoCodesWidget {...promoCodesWidgetProps} />
  );
  const paymentMethodsWidgetProps = usePaymentMethodsWidget({
    initializer,
    pageStore: useBuyNowPageStore,
    hooks: paymentMethodsHooks,
    isApplePayEnabled,
    billingStrategy,
    paymentStrategy,
    externalHooks: paymentMethodsExternalHooks,
  });

  const contactsTierSelectWidget = <ContactsTierSelectWidget {...contactsTierSelectWidgetProps} />;
  const currencyWidget = <CurrencyWidget {...currencyWidgetProps} />;
  const nonProfitWidget = <NonProfitWidget {...nonProfitWidgetProps} />;
  const orderSummaryProps = useOrderSummaryWidget({
    initializer,
    pageStore: useBuyNowPageStore,
    hooks: orderSummaryHooks,
    externalHooks: orderSummaryExternalHooks,
    billingStrategy,
    flowId: AccountModificationFlowId.Purchase,
  });

  const paymentButtonWidgetProps = usePaymentButtonWidget({
    initializer,
    pageStore: useBuyNowPageStore,
    hooks: paymentButtonHooks,
    externalHooks: paymentButtonExternalHooks,
    billingStrategy,
  });
  const paymentButtonWidget = <PaymentButtonWidget {...paymentButtonWidgetProps} />;
  const pricingConfiguratorProps = usePricingConfiguratorWidget({
    initializer,
  });
  const prepaySelectProps = usePrepaySelectWidget({
    initializer,
    pageStore: useBuyNowPageStore,
    hooks: prepaySelectHooks,
    flags: {
      billingStrategy,
      isTrialer,
      disallowSelectionOfShorterPaymentPlan: isStripePhase2Enabled ?? false,
    },
    flowId: AccountModificationFlowId.Purchase,
  });
  const alertWidgetProps = useAlertWidget({
    initializer,
    pageStore: useBuyNowPageStore,
    hooks: alertHooks,
  });

  const emailPlansWidgetProps = useEmailPlansWidget({
    initializer,
    pageStore: useBuyNowPageStore,
    hooks: emailPlansHooks,
    externalHooks: emailPlansExternalHooks,
    billingStrategy,
    paymentStrategy,
  });

  const billingInfoFormWidgetProps = useBillingInfoFormWidget({
    initializer,
    pageStore: useBuyNowPageStore,
    hooks: billingInfoFormHooks,
    billingStrategy,
  });

  // router non-ui widget hook
  useRouterNonUiWidget({
    initializer,
    isStripeEnabled,
    isStripePhase2Enabled: isStripePhase2Enabled ?? false,
    externalHooks: routerNonUiExternalHooks,
  });

  useInboundPromoCodesWidget({ initializer, pageStore: useBuyNowPageStore });

  return (
    <div className="payflow-buy-now">
      {/* NON UI START */}
      <EmailPlansWidget {...emailPlansWidgetProps} pricingTierDialogOnly />
      {/* NON UI END */}

      <div className="payflow-buy-now__alerts-wrapper">
        <AlertWidget {...alertWidgetProps} />
      </div>

      <div className="payflow-buy-now__top-wrapper">
        <PaymentProcessingInfoWidget {...paymentProcessingInfoWidgetProps} />
        {!shouldHidePricingConfigurator && isTrialer !== undefined && (
          <PricingConfiguratorWidget
            {...pricingConfiguratorProps}
            prepaySelectWidget={shouldHidePrepaySelectWidget ? null : <PrepaySelectWidget {...prepaySelectProps} />}
            contactsTierSelectWidget={shouldHideContactsTierSelectWidget ? null : contactsTierSelectWidget}
            currencyWidget={shouldHideCurrencyWidget ? null : currencyWidget}
            nonProfitWidget={shouldHideNonProfitWidget ? null : nonProfitWidget}
          />
        )}
      </div>

      <div className="payflow-buy-now__bottom-wrapper">
        {isMobile && (
          <Card className="mobile-only">
            <OrderSummaryWidget {...orderSummaryProps} injectedMiddleComponent={promoCodesWidget} />
          </Card>
        )}

        <Card className="payflow-buy-now__left-wrapper">
          <PaymentMethodsWidget {...paymentMethodsWidgetProps} />
          <BillingInfoFormWidget {...billingInfoFormWidgetProps} />
        </Card>

        {isMobile && <Card className="mobile-only">{paymentButtonWidget}</Card>}

        {isDesktop && (
          <Card className="payflow-buy-now__right-wrapper desktop-only">
            <OrderSummaryWidget {...orderSummaryProps} injectedMiddleComponent={promoCodesWidget} />
            {paymentButtonWidget}
          </Card>
        )}
      </div>

      <iframe title="fos-iframe" src={initializer.getFosTrackingIframeSrc()} style={{ display: 'none' }} />
    </div>
  );
};
